import { Divider } from "antd";
import React, { useEffect, useState } from "react";
import { annualKwhFromMonthlyUsageData } from "../../../../helpers/utilityMonthlyDailyToAnnual";

interface MonthlyUsageTabPrams {
    MonthlyUsage: any;
    MonthlyUsageChange: (value: any) => void;
}

/** Matches `buildUsageAndBillFromUtilityDetails` short month keys so the grid is never empty. */
const DEFAULT_MONTH_ROWS = () =>
    Array.from({ length: 12 }, (_, i) => ({
        Month: new Date(0, i).toLocaleString("default", { month: "short" }),
        MonthUsage: 0,
        Amount: 0,
    }));

const MONTHLY_TABLE_COL_WIDTHS = ['16%', '17%', '17%', '16%', '17%', '17%'];
const COLUMNS_PER_ROW = 2;

export const MonthlyUsageTab = ({ MonthlyUsage, MonthlyUsageChange, disabled }: MonthlyUsageTabPrams & { disabled?: boolean }) => {

    const [rows, setRows] = useState<any[]>([]);
    const [annualUsage, setAnnualUsage] = useState<number>(0);
    const [annualAmount, setAnnualAmount] = useState<number>(0);

    useEffect(() => {
        if (!MonthlyUsage) {
            setRows([]);
            return;
        }
        let data = MonthlyUsage.Data;
        if (!Array.isArray(data) || data.length === 0) {
            const fresh = DEFAULT_MONTH_ROWS();
            MonthlyUsageChange({
                ...MonthlyUsage,
                Data: fresh,
                AnnualAmount: MonthlyUsage.AnnualAmount ?? 0,
            });
            return;
        }
        let tempRows = []
        for (let i = 0; i < data.length; i += COLUMNS_PER_ROW) {
            tempRows.push(data.slice(i, i + COLUMNS_PER_ROW));
        }
        setRows(tempRows);
        const sumAmount = data.reduce((s: number, r: any) => s + (Number(r.Amount ?? r.MonthAmount) || 0), 0);
        const fromDaily = annualKwhFromMonthlyUsageData(data);
        setAnnualUsage(fromDaily);
        setAnnualAmount(Number(MonthlyUsage?.AnnualAmount) || sumAmount);
    }, [MonthlyUsage]);

    const updateMonthlyUsage = (item: any, event: any) => {
        const raw = event.target.value;
        const parsed = raw === '' || raw === null ? 0 : Number(raw);
        item.MonthUsage = Number.isFinite(parsed) ? parsed : 0;

        const data = MonthlyUsage?.Data || [];
        data.forEach((row: any) => {
            if (row.Month === item.Month) {
                row.MonthUsage = item.MonthUsage;
            }
        });
        const tempRows = [];
        for (let i = 0; i < data.length; i += COLUMNS_PER_ROW) {
            tempRows.push(data.slice(i, i + COLUMNS_PER_ROW));
        }
        setRows(tempRows);
        const annualFromDaily = annualKwhFromMonthlyUsageData(data);
        setAnnualUsage(annualFromDaily);

        MonthlyUsage.AnnualUsage = annualFromDaily;
        MonthlyUsage.Data = data;

        MonthlyUsageChange(MonthlyUsage);
    }

    const updateMonthlyAmount = (item: any, event: any) => {
        // update monthly amount
        item.Amount = Number(event.target.value);

        const data = MonthlyUsage?.Data || [];
        data.forEach((row: any) => {
            if (row.Month === item.Month) {
                row.Amount = item.Amount;
            }
        })
        const tempRows = [];
        for (let i = 0; i < data.length; i += COLUMNS_PER_ROW) {
            tempRows.push(data.slice(i, i + COLUMNS_PER_ROW));
        }
        setRows(tempRows);
        let total = 0;
        data.forEach((row: any) => {
            total += row.Amount;
        })
        setAnnualAmount(total);
        MonthlyUsage.AnnualAmount = total;
        MonthlyUsage.Data = data;

        MonthlyUsageChange(MonthlyUsage);
    }

    return (
        <div className="obd-utility-popup__usage-table-wrap">
            <table className="obd-utility-usage-table">
                <colgroup>
                    {MONTHLY_TABLE_COL_WIDTHS.map((width, idx) => (
                        <col key={idx} style={{ width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr>
                        <th>Month</th>
                        <th>Daily kWh</th>
                        <th>Amount ($)</th>
                        <th>Month</th>
                        <th>Daily kWh</th>
                        <th>Amount ($)</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {row.map((item: any, index: number) => (
                                <React.Fragment key={index}>
                                    <td className="obd-utility-usage-table__month">{item.Month}</td>
                                    <td>
                                        <input type="text"
                                            className="obd-utility__input"
                                            value={item.MonthUsage}
                                            onChange={(e) => updateMonthlyUsage(item, e)}
                                            disabled={disabled}
                                        />
                                    </td>
                                    <td>
                                        <input type="text"
                                            className="obd-utility__input"
                                            value={item.Amount}
                                            onChange={(e) => updateMonthlyAmount(item, e)}
                                            disabled={disabled}
                                        />
                                    </td>
                                </React.Fragment>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
            <Divider orientation="right">
                <span className='obd-utility__field-label'>
                    Est. annual kWh (daily × days/mo): {annualUsage} · Total amount: ${annualAmount}
                </span>
            </Divider>
        </div>
    );
};